import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Delete, ArrowLeft } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ThemeSwitch from "@/theme/ThemeSwitch";
import { Card, Button, Avatar } from "@/ui";

// Shared counter device: pick your name, tap your 4-digit PIN, start your shift.
const DEMO_SHOP = "shop-tita-baby";

export default function CounterSignin() {
  const { staffPin } = useAuth();
  const navigate = useNavigate();
  const shopId = localStorage.getItem("reto_counter_shop") || DEMO_SHOP;
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [picked, setPicked] = useState(null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/auth/staff-list?shopId=${shopId}`).then((r) => setInfo(r.data)).catch((e) => setError(friendlyError(e)));
  }, [shopId]);

  const tap = (d) => setPin((p) => (p.length >= 4 ? p : p + d));
  const back = () => setPin((p) => p.slice(0, -1));

  const start = async () => {
    setBusy(true); setError("");
    const r = await staffPin(shopId, picked.id, pin);
    setBusy(false);
    if (r.ok) navigate("/counter", { replace: true });
    else { setError(r.message); setPin(""); }
  };

  return (
    <div className="shell">
      <div className="app-bg" aria-hidden="true" />
      <div className="page" style={{ margin: "0 auto", width: "100%", maxWidth: 420 }}>
        <div className="row-between" style={{ marginBottom: 16 }}>
          <div className="row"><span className="brand-mark">R</span><span className="t-h2">{info?.shopName || "Counter"}</span></div>
          <ThemeSwitch />
        </div>
        {error ? <div className="err-text" data-testid="counter-signin-error" style={{ marginBottom: 12 }}>{error}</div> : null}

        {!picked ? (
          <Card>
            <h1 className="t-h2" style={{ marginBottom: 12 }}>who's on the counter?</h1>
            <div className="stack" data-testid="staff-list">
              {(info?.staff || []).map((s) => (
                <button key={s.id} className="sheet-item" data-testid={`staff-${s.id}`} onClick={() => { setPicked(s); setPin(""); setError(""); }}>
                  <Avatar name={s.name} /><span className="t-body">{s.name}</span>
                </button>
              ))}
            </div>
            <p className="muted-3 t-small" style={{ marginTop: 16 }}><Link to="/login" data-testid="back-to-email">the owner signs in with email instead</Link></p>
          </Card>
        ) : (
          <Card>
            <button className="btn btn-ghost btn-sm" data-testid="pick-again" onClick={() => { setPicked(null); setPin(""); }}><ArrowLeft size={20} strokeWidth={1.75} />not you?</button>
            <div style={{ textAlign: "center", margin: "12px 0 16px" }}>
              <Avatar name={picked.name} />
              <h2 className="t-h2" style={{ marginTop: 8 }}>hi {picked.name}</h2>
              <p className="muted t-small">tap your 4-digit PIN to start your shift</p>
              <div className="row" style={{ justifyContent: "center", gap: 12, margin: "16px 0" }} data-testid="pin-dots">
                {[0, 1, 2, 3].map((i) => (
                  <span key={i} style={{ width: 14, height: 14, borderRadius: 999, border: "1px solid var(--border-strong)", background: pin.length > i ? "var(--accent)" : "transparent" }} />
                ))}
              </div>
            </div>
            <div className="grid" style={{ gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                <Button key={n} data-testid={`pin-${n}`} onClick={() => tap(String(n))} style={{ height: 56, fontSize: 20 }}>{n}</Button>
              ))}
              <Button data-testid="pin-back" onClick={back} style={{ height: 56 }}><Delete size={20} strokeWidth={1.75} /></Button>
              <Button data-testid="pin-0" onClick={() => tap("0")} style={{ height: 56, fontSize: 20 }}>0</Button>
              <Button variant="primary" data-testid="start-shift" onClick={start} loading={busy} disabled={pin.length !== 4} style={{ height: 56 }}>start</Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
